/**
 * RBAC-5.3 · NFR-6b. "A lockout is noticed before it is reported."
 *
 * A superadmin-only header button to Account health, carrying a count of accounts that need attention
 * now. The count is what makes the list noticed: a page nobody opens notices nothing. It reads the
 * same `degraded_accounts()` the page does, under the same query key, so opening the page and acting
 * there updates the badge.
 */

import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ACCOUNT_HEALTH_PATH, DEGRADED_ACCOUNTS_QUERY_KEY, criticalCount } from '@/lib/accountRecovery';
import { fetchDegradedAccounts } from '@/services/accountRecoveryService';

export function AccountHealthButton() {
  const location = useLocation();
  const { data } = useQuery({
    queryKey: DEGRADED_ACCOUNTS_QUERY_KEY,
    queryFn: fetchDegradedAccounts,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const n = data ? criticalCount(data) : 0;
  const label = n > 0 ? `Account health: ${n} ${n === 1 ? 'account needs' : 'accounts need'} attention` : 'Account health';

  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className={cn(location.pathname === ACCOUNT_HEALTH_PATH ? 'bg-primary/20 text-primary' : 'relative')}
      title={label}
    >
      <Link to={ACCOUNT_HEALTH_PATH} className="relative">
        <ShieldAlert className="h-5 w-5" />
        {n > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            aria-hidden="true"
          >
            {n}
          </span>
        )}
        <span className="sr-only">{label}</span>
      </Link>
    </Button>
  );
}
